/* ==========================================================================
   The Flower Club Workshop — sessions picker.

   Everything the page does at runtime lives here:
     - the city tabs filter the session rows (selection survives filtering)
     - picking a row rewrites the line under the button to name your seat
     - the button posts one seat to /cart/add, then either goes straight to
       checkout or opens the theme's cart drawer, per the section setting
     - the closing band's rows select an evening and scroll back up to it

   The markup works without any of this. Rows are <label>s around real radios
   named `id`, so with JavaScript off the form still posts a variant to
   /cart/add, and the closing rows are plain anchors to the sessions section.
   ========================================================================== */

if (!customElements.get('fb-workshop-sessions')) {
  customElements.define(
    'fb-workshop-sessions',
    class FbWorkshopSessions extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-fbw-form]');
        this.rows = Array.from(this.querySelectorAll('[data-fbw-row]'));
        this.tabs = Array.from(this.querySelectorAll('[data-fbw-tab]'));
        this.empties = Array.from(this.querySelectorAll('[data-fbw-empty]'));
        this.button = this.querySelector('[data-fbw-submit]');
        this.noteLine = this.querySelector('[data-fbw-note-seat]');
        this.errorLine = this.querySelector('[data-fbw-error]');

        this.seatTemplate = this.dataset.seatNote || 'Your seat: [session].';
        this.addedTemplate = this.dataset.addedNote || 'Seat saved to cart: [session].';
        this.added = false;

        this.addEventListener('change', this.onChange.bind(this));
        this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));
        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

        // The terms tick is a `required` input, so the browser blocks the
        // submit on its own and this handler never runs when it is missing.
        // All this does is trade the browser's bubble for the page's own line.
        this.consent = this.querySelector('[data-fbw-consent]');
        if (this.consent) {
          this.consent.addEventListener('invalid', (event) => {
            event.preventDefault();
            this.showError(this.dataset.consentError || 'Please tick to go on.');
            this.consent.focus();
          });
          this.consent.addEventListener('change', () => {
            if (this.consent.checked) this.clearError();
          });
        }

        // The closing band is a separate section, so reach it through the
        // document rather than trying to own its markup.
        this.onJumpClick = this.onJumpClick.bind(this);
        document.addEventListener('click', this.onJumpClick);

        this.filter(this.dataset.defaultCity || 'all');
        this.renderNote();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onJumpClick);
      }

      /* ---- selection ---- */

      get selectedRow() {
        const radio = this.querySelector('[data-fbw-radio]:checked');
        return radio ? radio.closest('[data-fbw-row]') : null;
      }

      select(variantId) {
        const row = this.rows.find((r) => r.dataset.variantId === String(variantId));
        if (!row) return false;

        const radio = row.querySelector('[data-fbw-radio]');
        if (!radio || radio.disabled) return false;

        radio.checked = true;
        this.added = false;
        this.renderNote();
        return true;
      }

      onChange(event) {
        if (!event.target.matches('[data-fbw-radio]')) return;
        // A fresh choice is not yet in the cart, whatever the last one was.
        this.added = false;
        this.clearError();
        this.renderNote();
      }

      renderNote() {
        if (!this.noteLine) return;

        const row = this.selectedRow;
        if (!row) {
          this.noteLine.textContent = '';
          return;
        }

        const template = this.added ? this.addedTemplate : this.seatTemplate;
        this.noteLine.textContent = template.replace('[session]', row.dataset.sessionLabel || '');
      }

      /* ---- city tabs ---- */

      onTabClick(event) {
        event.preventDefault();
        this.filter(event.currentTarget.dataset.city || 'all');
      }

      filter(city) {
        const key = String(city).toLowerCase();

        this.tabs.forEach((tab) => {
          const isActive = (tab.dataset.city || '').toLowerCase() === key;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        let shown = 0;
        this.rows.forEach((row) => {
          const match = key === 'all' || (row.dataset.city || '').toLowerCase() === key;
          row.hidden = !match;
          if (match) shown += 1;
        });

        // Only the empty state belonging to the filtered city may show, and
        // only when that filter turned up nothing.
        this.empties.forEach((empty) => {
          const belongs = (empty.dataset.city || '').toLowerCase() === key;
          empty.hidden = !(belongs && shown === 0);
        });

        // Filtering never changes what is selected — someone can compare cities
        // without losing the evening they had picked.
      }

      /* ---- closing band ---- */

      onJumpClick(event) {
        const trigger = event.target.closest('[data-fbw-jump]');
        if (!trigger) return;

        const variantId = trigger.dataset.variantId;
        if (!variantId || !this.select(variantId)) return;

        event.preventDefault();
        this.scrollIntoViewSmoothly();
      }

      scrollIntoViewSmoothly() {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const top = this.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
      }

      /* ---- add to cart ---- */

      onSubmit(event) {
        // No fetch, no interception: let the browser post the form.
        if (!window.fetch) return;

        event.preventDefault();
        if (this.loading) return;

        const radio = this.querySelector('[data-fbw-radio]:checked');
        if (!radio) return;

        this.setLoading(true);
        this.clearError();

        // Straight to checkout means the drawer never opens, so there is no
        // point paying for its sections in the add-to-cart response.
        const toCheckout = this.hasAttribute('data-direct-checkout');
        const cartNotification = toCheckout ? null : document.querySelector('cart-notification');
        const sections =
          cartNotification && typeof cartNotification.getSectionsToRender === 'function'
            ? cartNotification.getSectionsToRender().map((section) => section.id)
            : [];

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify({
            items: [{ id: Number(radio.value), quantity: 1, properties: this.lineItemProperties() }],
            sections: sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((state) => {
            if (state.status) {
              // Shopify returns `status` only on a rejected add — most often
              // the last seat went while this page was open.
              this.showError(state.description || state.message);
              return;
            }

            if (toCheckout) {
              // Leave the button busy: the page is on its way out, and letting
              // it go idle invites a second click that adds a second seat.
              this.redirecting = true;
              window.location.href = this.dataset.checkoutUrl || '/checkout';
              return;
            }

            this.added = true;
            this.renderNote();

            if (cartNotification && typeof cartNotification.renderContents === 'function' && state.sections) {
              cartNotification.renderContents(state);
              if (typeof cartNotification.open === 'function') cartNotification.open();
            } else {
              this.refreshCartCount();
            }
          })
          .catch(() => {
            this.showError(this.dataset.genericError || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            if (!this.redirecting) this.setLoading(false);
          });
      }

      /* Read every properties[...] field out of the form so the AJAX add records
         the same thing the plain form post would. Written generically rather
         than reaching for the consent tick by name, so a term added later needs
         no change here. */
      lineItemProperties() {
        const props = {};
        this.form.querySelectorAll('[name^="properties["]').forEach((field) => {
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
          if (!field.value) return;
          props[field.name.slice('properties['.length, -1)] = field.value;
        });
        return props;
      }

      refreshCartCount() {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
              el.textContent = cart.item_count;
            });
          })
          .catch(() => {});
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        if (this.button) this.button.toggleAttribute('aria-busy', isLoading);
      }

      showError(message) {
        if (!this.errorLine || !message) return;
        this.errorLine.textContent = message;
        this.errorLine.hidden = false;
      }

      clearError() {
        if (!this.errorLine) return;
        this.errorLine.textContent = '';
        this.errorLine.hidden = true;
      }
    }
  );
}
